/**
 * useAttributeDialogs — Track 3 Phase 6
 *
 * Owns the three attribute-editing dialogs extracted from `App.tsx` as
 * part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 *   - ATTDEF                 → Attribute Definition dialog
 *   - DDATTE / ATTEDIT       → Edit Attributes dialog
 *   - EATTEDIT               → Enhanced Attribute Editor dialog
 *
 * Responsibilities:
 *   - Owns the three open flags (`attdefDialogOpen`,
 *     `editAttributesDialogOpen`, `eatteditDialogOpen`).
 *   - Mirrors `draftSession.step` into the open flags so the draft
 *     state machine drives auto-open (ATTDEF / DDATTE / EATTEDIT all
 *     enter via a draft session step). Clearing the draft session
 *     also clears all three flags — matches the pre-extraction
 *     behaviour at App.tsx ~3062-3071.
 *   - Owns the Confirm / Cancel / Apply handlers — each dispatches
 *     the matching `geometry/...` command, emits a localized
 *     command-feed entry, and tears down the draft session.
 *   - Exposes `closeAll()` so `resetShellState()` can drop all three
 *     dialogs in one call (matches the previous direct setter
 *     sequence at App.tsx ~3047-3049).
 *
 * Returned shape:
 *   {
 *     attdef:          { isOpen, props: { onConfirm, onCancel } },
 *     editAttributes:  { isOpen, props: { onConfirm, onCancel } },
 *     eattedit:        { isOpen, props: { onApply, onConfirm, onCancel } },
 *     closeAll,
 *   }
 *
 * The orchestrator composes the dialog JSX (it owns the
 * `<select>`-based resolver `resolveSelectedInsertForAttributeEditing`
 * and the textStyles / layers props bundle) — the hook only owns the
 * open flag and the three handler trios so App.tsx stops calling the
 * raw `setAttdefDialogOpen` / `setEditAttributesDialogOpen` /
 * `setEatteditDialogOpen` setters.
 *
 * AutoCAD parity:
 *   ATTDEF must run inside a BEDIT session (gated below); DDATTE /
 *   EATTEDIT both target an existing INSERT regardless of space. The
 *   resolver lives in App.tsx because it depends on
 *   `selectedEntities`, `geometryDocument`, and
 *   `draftSession.options.targetEntityId` — which are too widely
 *   coupled for this Phase 6 mechanical extraction. A future phase
 *   may lift the resolver into the hook once selection + active-space
 *   plumbing also moves to a hook.
 */

import { useCallback, useEffect, useState } from "react";

import type {
  AttributeDefinitionParams,
} from "../components/AttributeDefinitionDialog";
import type { EditAttributesParams } from "../components/EditAttributesDialog";
import type { EnhancedAttributeEditorParams } from "../components/EnhancedAttributeEditorDialog";
import type { DraftSession } from "../scene/draftingHelpers";
import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";

export interface UseAttributeDialogsParams {
  /** Drives auto-open via `draftSession.step`. */
  draftSession: DraftSession | null;
  /** Cleared by every Confirm / Cancel handler. */
  setDraftSession: (session: DraftSession | null) => void;
  /** Cleared by every Confirm / Cancel handler. */
  setCommandText: (text: string) => void;
  /** Receives localized "attdef_created" / "attributes_updated" feed entries. */
  appendCommandFeed: (text: string) => void;
}

export interface UseAttributeDialogsResult {
  attdef: {
    isOpen: boolean;
    props: {
      onConfirm: (params: AttributeDefinitionParams) => void;
      onCancel: () => void;
    };
  };
  editAttributes: {
    isOpen: boolean;
    props: {
      onConfirm: (params: EditAttributesParams) => void;
      onCancel: () => void;
    };
  };
  eattedit: {
    isOpen: boolean;
    props: {
      onApply: (params: EnhancedAttributeEditorParams) => void;
      onConfirm: (params: EnhancedAttributeEditorParams) => void;
      onCancel: () => void;
    };
  };
  /** Drops all three dialogs — used by `resetShellState()`. */
  closeAll: () => void;
}

export function useAttributeDialogs({
  draftSession,
  setDraftSession,
  setCommandText,
  appendCommandFeed,
}: UseAttributeDialogsParams): UseAttributeDialogsResult {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);
  const state = useEditorStore((store) => store.state);

  const [attdefDialogOpen, setAttdefDialogOpen] = useState(false);
  const [editAttributesDialogOpen, setEditAttributesDialogOpen] =
    useState(false);
  const [eatteditDialogOpen, setEatteditDialogOpen] = useState(false);

  // Auto-open mirror of the draft state machine. Matches the
  // pre-extraction effect at App.tsx ~3062-3071.
  useEffect(() => {
    setAttdefDialogOpen(draftSession?.step === "attdef-dialog");
    setEditAttributesDialogOpen(draftSession?.step === "ddatte-dialog");
    setEatteditDialogOpen(draftSession?.step === "eattedit-dialog");
    if (!draftSession) {
      setAttdefDialogOpen(false);
      setEditAttributesDialogOpen(false);
      setEatteditDialogOpen(false);
    }
  }, [draftSession]);

  const closeAll = useCallback(() => {
    setAttdefDialogOpen(false);
    setEditAttributesDialogOpen(false);
    setEatteditDialogOpen(false);
  }, []);

  // ── ATTDEF ───────────────────────────────────────────────────
  const onAttdefConfirm = useCallback(
    (params: AttributeDefinitionParams) => {
      const blockEditSpace = geometryDocument.spaces.find(
        (s) => s.kind === "block-edit",
      );
      if (!blockEditSpace || !blockEditSpace.blockEditName) {
        appendCommandFeed(t("toast.attdef.not_in_block_editor"));
        setAttdefDialogOpen(false);
        setDraftSession(null);
        setCommandText("");
        return;
      }
      dispatch({
        type: "geometry/create-attdef",
        source: "toolbar",
        blockName: blockEditSpace.blockEditName,
        attdef: {
          tag: params.tag,
          prompt: params.prompt,
          defaultValue: params.defaultValue,
          insertionPoint: {
            x: params.insertionPoint.x,
            y: params.insertionPoint.y,
          },
          height: params.height,
          rotation: params.rotation,
          widthFactor: params.widthFactor,
          obliqueAngle: params.obliqueAngle,
          styleName: params.styleName,
          hJustification: params.hJustification,
          vJustification: params.vJustification,
          alignmentPoint: params.alignmentPoint,
          flags: params.flags,
          backgroundFill: params.backgroundFill,
          backgroundColor: params.backgroundColor,
          fillBoxScale: params.fillBoxScale,
        },
        layer: state.propertyWorkflows.defaultObjectProperties.Layer ?? "0",
        // CECOLOR (round-5 FINDING B) — omit `color` so the dispatch chokepoint
        // stamps the live current color onto the ATTDEF (it is a DRAWN entity
        // that inherits CECOLOR in AutoCAD), exactly as the text / mtext /
        // dimension toolbar paths omit color and let the stamp reach them.
        linetype: "ByLayer",
        lineweight: "ByLayer",
      });
      appendCommandFeed(
        t("summary.attdef_created", {
          tag: params.tag,
          block: blockEditSpace.blockEditName,
        }),
      );
      setAttdefDialogOpen(false);
      setDraftSession(null);
      setCommandText("");
    },
    [
      dispatch,
      geometryDocument,
      state,
      appendCommandFeed,
      t,
      setDraftSession,
      setCommandText,
    ],
  );

  const onAttdefCancel = useCallback(() => {
    setAttdefDialogOpen(false);
    setDraftSession(null);
    setCommandText("");
  }, [setDraftSession, setCommandText]);

  // ── DDATTE / ATTEDIT ─────────────────────────────────────────
  const onEditAttributesConfirm = useCallback(
    (params: EditAttributesParams) => {
      dispatch({
        type: "geometry/update-attribute-values",
        source: "toolbar",
        insertId: params.insertId,
        updates: params.updates,
      });
      appendCommandFeed(
        t("summary.attributes_updated", { count: params.updates.length }),
      );
      setEditAttributesDialogOpen(false);
      setDraftSession(null);
      setCommandText("");
    },
    [dispatch, appendCommandFeed, t, setDraftSession, setCommandText],
  );

  const onEditAttributesCancel = useCallback(() => {
    setEditAttributesDialogOpen(false);
    setDraftSession(null);
    setCommandText("");
  }, [setDraftSession, setCommandText]);

  // ── EATTEDIT ─────────────────────────────────────────────────
  const onEatteditApply = useCallback(
    (params: EnhancedAttributeEditorParams) => {
      dispatch({
        type: "geometry/update-attribute-values",
        source: "toolbar",
        insertId: params.insertId,
        updates: params.updates,
        insertProperties: params.insertProperties,
      });
      appendCommandFeed(
        t("summary.attributes_updated", { count: params.updates.length }),
      );
    },
    [dispatch, appendCommandFeed, t],
  );

  const onEatteditConfirm = useCallback(
    (params: EnhancedAttributeEditorParams) => {
      onEatteditApply(params);
      setEatteditDialogOpen(false);
      setDraftSession(null);
      setCommandText("");
    },
    [onEatteditApply, setDraftSession, setCommandText],
  );

  const onEatteditCancel = useCallback(() => {
    setEatteditDialogOpen(false);
    setDraftSession(null);
    setCommandText("");
  }, [setDraftSession, setCommandText]);

  return {
    attdef: {
      isOpen: attdefDialogOpen,
      props: {
        onConfirm: onAttdefConfirm,
        onCancel: onAttdefCancel,
      },
    },
    editAttributes: {
      isOpen: editAttributesDialogOpen,
      props: {
        onConfirm: onEditAttributesConfirm,
        onCancel: onEditAttributesCancel,
      },
    },
    eattedit: {
      isOpen: eatteditDialogOpen,
      props: {
        onApply: onEatteditApply,
        onConfirm: onEatteditConfirm,
        onCancel: onEatteditCancel,
      },
    },
    closeAll,
  };
}
